import {
  PatientIdTypeEnum,
  PatientResponseIdentifier,
} from '@dermloop/static-values';
import {
  ArrayNotEmpty,
  IsArray,
  IsBoolean,
  IsEmail,
  IsEnum,
  IsInt,
  IsNotEmpty,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  Min,
  IsIn,
} from 'class-validator';
import { MutuallyExclusiveWith } from '@dermloop/utils';
import { BaseCreateDto, BaseDto, BaseUpdateDto } from './base.dto';
import { ConditionTypeDto } from './condition-type.dtos';
import { BlockPolicy, customLocales } from '@dermloop/static-values';
import type { DlCustomLocale } from '@dermloop/static-values';

/**
 * Upper bound (one year, in hours) for any reassignment-threshold value — the
 * federation default `reassignmentHours` and the per-member overrides. Guards
 * against fat-finger entries (e.g. an interval of millions of hours); also
 * bounds the `windowHours` field on the reassignment-alert DTOs.
 */
export const MAX_REASSIGNMENT_HOURS = 365 * 24;

export interface FederationDto extends BaseDto {
  name: string;
  userAffiliationOccupationAndSpecialisationEnabled: boolean;
  referralNotificationEmail?: string | null;
  priorSpecialistAssignmentLookbackDays?: number;
  noteRequired?: boolean;
  showDatetimeOnTeleEvaluation?: boolean;
  patientIdType: PatientIdTypeEnum;
  hidePatientId: boolean;
  useReferralClaims: boolean;
  conditionTypes: ConditionTypeDto[];
  /** @deprecated Use conditionTypes */
  skinConditionTypes?: ConditionTypeDto[];
  fieldConfigurationId: string;
  imageQualityAssessmentEnabled: boolean;
  healthRecordBlockPolicy: BlockPolicy;
  reassignmentHours: number;
  locale: DlCustomLocale;
  enableEconsultNotifications: boolean;
  hidePatientName: boolean;
  enableReferralIdDisplay: boolean;
  enableUrgencyRating: boolean;
  enableReferralTypeDescription: boolean;
  requireEconsultManualRelease: boolean;
  nspAuditEnabled: boolean;
  blockOnNspAuditFailEnabled: boolean;
  nspAuditBrsCheckDisabled: boolean;
  enableTeleAssessmentRating: boolean;
  timeSpentReviewingTeleassessmentsInputEnabled: boolean;
  seranoteDictationEnabled: boolean;
  teleFollowUpQuestionEnabled: boolean;
  patientSmsTeleResponseEnabled: boolean;
  patientAddressEnabled: boolean;
  skinCheckHistoryQuestionsEnabled: boolean;
  patientResponseIdentifier?: PatientResponseIdentifier;
  requireSor: boolean;
  inactivityDeactivationDays: number;
  logoImageIdentifier?: string;
  logoImageUrl?: string;
  federationNameInPdfTitleEnabled: boolean;
}

export class CreateFederationDto
  extends BaseCreateDto
  implements Partial<FederationDto>
{
  @IsNotEmpty()
  @IsString()
  name: string;

  @IsOptional()
  @IsBoolean()
  userAffiliationOccupationAndSpecialisationEnabled?: boolean;

  @IsOptional()
  @IsInt()
  priorSpecialistAssignmentLookbackDays?: number;

  @IsOptional()
  @IsEmail()
  referralNotificationEmail?: string;

  @IsOptional()
  @IsBoolean()
  noteRequired?: boolean;

  @IsOptional()
  @IsBoolean()
  showDatetimeOnTeleEvaluation?: boolean;

  @IsEnum(PatientIdTypeEnum)
  patientIdType: PatientIdTypeEnum;

  @IsOptional()
  @IsBoolean()
  hidePatientId?: boolean;

  @IsBoolean()
  useReferralClaims: boolean;

  @IsOptional()
  @IsArray()
  @ArrayNotEmpty()
  @IsUUID('all', { each: true })
  conditionTypeIds?: string[];

  @IsOptional()
  @IsArray()
  @ArrayNotEmpty()
  @IsUUID('all', { each: true })
  /** @deprecated Use conditionTypeIds */
  skinConditionTypeIds?: string[];

  @IsUUID()
  fieldConfigurationId: string;

  @IsBoolean()
  imageQualityAssessmentEnabled: boolean;

  @IsEnum(BlockPolicy)
  healthRecordBlockPolicy: BlockPolicy;

  @IsInt()
  @Min(1)
  @Max(MAX_REASSIGNMENT_HOURS)
  reassignmentHours: number;

  @IsIn(customLocales)
  locale: DlCustomLocale;

  @IsBoolean()
  enableEconsultNotifications: boolean;

  @IsBoolean()
  hidePatientName: boolean;

  @IsBoolean()
  enableReferralIdDisplay: boolean;

  @IsBoolean()
  enableUrgencyRating: boolean;

  @IsBoolean()
  enableReferralTypeDescription: boolean;

  @IsBoolean()
  requireEconsultManualRelease: boolean;

  @IsBoolean()
  nspAuditEnabled: boolean;

  @IsBoolean()
  blockOnNspAuditFailEnabled: boolean;

  @IsBoolean()
  nspAuditBrsCheckDisabled: boolean;

  @IsBoolean()
  enableTeleAssessmentRating: boolean;

  @IsOptional()
  @IsBoolean()
  timeSpentReviewingTeleassessmentsInputEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  seranoteDictationEnabled?: boolean;

  /** It's been decided by management that patient sms tele response and follow up question can't be enabled at the same time**/
  @IsOptional()
  @IsBoolean()
  @MutuallyExclusiveWith<CreateFederationDto>('patientSmsTeleResponseEnabled')
  teleFollowUpQuestionEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  @MutuallyExclusiveWith<CreateFederationDto>('teleFollowUpQuestionEnabled')
  patientSmsTeleResponseEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  patientAddressEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  skinCheckHistoryQuestionsEnabled?: boolean;

  @IsOptional()
  @IsEnum(PatientResponseIdentifier)
  patientResponseIdentifier?: PatientResponseIdentifier;

  @IsBoolean()
  requireSor: boolean;

  @IsInt()
  @Min(1)
  inactivityDeactivationDays: number;

  @IsOptional()
  @IsString()
  logoImageIdentifier?: string;

  @IsBoolean()
  federationNameInPdfTitleEnabled: boolean;
}

export class UpdateFederationDto
  extends BaseUpdateDto
  implements Partial<FederationDto>
{
  @IsNotEmpty()
  @IsString()
  name: string;

  @IsOptional()
  @IsEmail()
  referralNotificationEmail?: string | null;

  @IsBoolean()
  userAffiliationOccupationAndSpecialisationEnabled: boolean;

  @IsOptional()
  @IsInt()
  priorSpecialistAssignmentLookbackDays?: number;

  @IsOptional()
  @IsBoolean()
  noteRequired?: boolean;

  @IsOptional()
  @IsBoolean()
  showDatetimeOnTeleEvaluation?: boolean;

  @IsOptional()
  @IsEnum(PatientIdTypeEnum)
  patientIdType?: PatientIdTypeEnum;

  @IsOptional()
  @IsBoolean()
  hidePatientId?: boolean;

  @IsOptional()
  @IsBoolean()
  useReferralClaims?: boolean;

  @IsOptional()
  @IsArray()
  @IsUUID('all', { each: true })
  conditionTypeIds?: string[];

  @IsOptional()
  @IsArray()
  @IsUUID('all', { each: true })
  /** @deprecated Use conditionTypeIds */
  skinConditionTypeIds?: string[];

  @IsOptional()
  @IsUUID()
  fieldConfigurationId?: string;

  @IsOptional()
  @IsBoolean()
  imageQualityAssessmentEnabled?: boolean;

  @IsOptional()
  @IsEnum(BlockPolicy)
  healthRecordBlockPolicy?: BlockPolicy;

  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(MAX_REASSIGNMENT_HOURS)
  reassignmentHours?: number;

  @IsOptional()
  @IsIn(customLocales)
  locale?: DlCustomLocale;

  @IsOptional()
  @IsBoolean()
  enableEconsultNotifications?: boolean;

  @IsOptional()
  @IsBoolean()
  hidePatientName?: boolean;

  @IsOptional()
  @IsBoolean()
  enableReferralIdDisplay?: boolean;

  @IsOptional()
  @IsBoolean()
  enableUrgencyRating?: boolean;

  @IsOptional()
  @IsBoolean()
  enableReferralTypeDescription?: boolean;

  @IsOptional()
  @IsBoolean()
  requireEconsultManualRelease?: boolean;

  @IsOptional()
  @IsBoolean()
  nspAuditEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  blockOnNspAuditFailEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  nspAuditBrsCheckDisabled?: boolean;

  @IsOptional()
  @IsBoolean()
  enableTeleAssessmentRating?: boolean;

  @IsOptional()
  @IsBoolean()
  timeSpentReviewingTeleassessmentsInputEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  seranoteDictationEnabled?: boolean;

  /** It's been decided by management that patient sms tele response and follow up question can't be enabled at the same time**/
  @IsOptional()
  @IsBoolean()
  @MutuallyExclusiveWith<UpdateFederationDto>('patientSmsTeleResponseEnabled')
  teleFollowUpQuestionEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  @MutuallyExclusiveWith<UpdateFederationDto>('teleFollowUpQuestionEnabled')
  patientSmsTeleResponseEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  patientAddressEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  skinCheckHistoryQuestionsEnabled?: boolean;

  @IsOptional()
  @IsEnum(PatientResponseIdentifier)
  patientResponseIdentifier?: PatientResponseIdentifier;

  @IsOptional()
  @IsBoolean()
  requireSor?: boolean;

  @IsOptional()
  @IsInt()
  @Min(1)
  inactivityDeactivationDays?: number;

  @IsOptional()
  @IsString()
  logoImageIdentifier?: string;

  @IsOptional()
  @IsBoolean()
  federationNameInPdfTitleEnabled?: boolean;
}

const ALLOWED_LOGO_CONTENT_TYPES = ['image/png', 'image/jpeg'] as const;
export type FederationLogoContentType =
  (typeof ALLOWED_LOGO_CONTENT_TYPES)[number];

export class FederationLogoUploadUrlRequestDto {
  @IsIn(ALLOWED_LOGO_CONTENT_TYPES)
  contentType: FederationLogoContentType;
}

export type FederationLogoUploadUrlDto = {
  url: string;
  fileId: string;
};

export class ConditionTypeRemovalImpactRequestDto {
  @IsArray()
  @ArrayNotEmpty()
  @IsUUID('all', { each: true })
  conditionTypeIds: string[];
}

export type ConditionTypeRemovalImpactMedicalUnitDto = {
  id: string;
  name: string;
};

/**
 * What removing one condition type from a federation would do: the units
 * that lose it, the units that keep its row because no other type they hold
 * is still offered (remembered for re-enabling, but granting nothing
 * meanwhile), and how many user affiliations hold it in their own grant.
 */
export type ConditionTypeRemovalImpactDto = {
  conditionTypeId: string;
  conditionTypeName: string;
  medicalUnitsLosing: ConditionTypeRemovalImpactMedicalUnitDto[];
  medicalUnitsKeeping: ConditionTypeRemovalImpactMedicalUnitDto[];
  affiliationCount: number;
};
